import { useMemo } from 'react';
import { TextBadge } from '@carvana/ds-badge';
import { LifecycleState } from 'api/GraphQL/queries/getVehicleSummaries';

type UseBadgeParams = {
  lifecycleState: LifecycleState;
  daysToExpiration?: number;
};

/**
 * Custom hook to determine the badge type based on lifecycle state and vehicle value.
 * Returns the appropriate badge for display and navigation logic.
 */
export const useBadge = ({ lifecycleState, daysToExpiration = 0 }: UseBadgeParams) =>
  useMemo(() => {
    // Determine heading based on lifecycle status
    switch (lifecycleState) {
      case LifecycleState.Scheduled:
        return (
          <TextBadge priority="informational" variant="outline">
            Appointment scheduled
          </TextBadge>
        );
      case LifecycleState.AppliedToPurchase:
        return (
          <TextBadge priority="informational" variant="outline">
            Applied to Trade In
          </TextBadge>
        );
      case LifecycleState.Complete:
        return (
          <TextBadge priority="success" variant="subtle">
            Sold to Carvana
          </TextBadge>
        );
      case LifecycleState.Expired:
        return (
          <TextBadge priority="critical" variant="strong">
            Expired
          </TextBadge>
        );

      case LifecycleState.Rejected:
      case LifecycleState.NeedsManualAppraisal:
      case LifecycleState.DeclinedReprice:
        return (
          <TextBadge priority="critical" variant="outline">
            Action required
          </TextBadge>
        );
      // Default case for any other lifecycle states
      case LifecycleState.Active:
      default:
        return (
          <TextBadge priority={daysToExpiration <= 3 ? 'warning' : 'success'} variant="outline">
            {daysToExpiration <= 0
              ? 'Expires today'
              : `Expires in ${daysToExpiration} ${daysToExpiration === 1 ? 'day' : 'days'}`}
          </TextBadge>
        );
    }
  }, [lifecycleState, daysToExpiration]);
